import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Query } from 'react-apollo';
import Box from '@material-ui/core/Box';
import { makeStyles } from '@material-ui/core/styles';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Avatar from '@material-ui/core/Avatar';
import PersonIcon from '@material-ui/icons/Person';
import Button from 'components/common/Button';
import Spin from 'components/common/Spin';
import { GET_STUDENT_PROFILE } from 'graphql/student/Profile';
import {
  GET_CONFERENCES,
  GET_CIRCLE_CONFERENCES,
  GET_COACHES_CONFERENCES,
  GET_CIRCLE_COACH_CONFERENCES
} from 'graphql/conference';
import { USER_TYPES } from 'constant/userTypes';
import humanDuration from 'humanize-duration';
import get from 'lodash/get';
import uniq from 'lodash/uniq';
import { toUserTimezone } from 'utils';
import Helmet from 'react-helmet';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import { filter } from 'lodash';
import BioCard from 'modules/student/team/components/BioCard';
import SessionsHistory from 'modules/student/team/components/SessionsHistory';

const useStyles = makeStyles((theme) => ({
  profileCard: {
    height: `100%`,
  },
  card: {
    marginBottom: theme.spacing(4),
  },
  cardContent: {
    padding: theme.spacing(4),
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
  avatar: {
    width: 108,
    height: 108,
    borderRadius: '50%',
    border: '5px solid #E9ECF1',
    marginRight: theme.spacing(3),
  },
  bio: {
    marginBottom: theme.spacing(4),
  },
  action: {
    textAlign: 'center',
  },
  listHeader: {
    marginBottom: theme.spacing(4),
  },
  listItem: {
    display: 'flex',
    marginBottom: theme.spacing(4),
  },
  media: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f8f8f8',
    width: 150,
    height: 100,
    marginRight: theme.spacing(3),
    borderRadius: 8,
  },
  movie: {
    fontSize: 100,
    opacity: 0.5,
  },
  appBar: {
    position: 'relative',
  },
  modalBody: {
    padding: 16,
  },
}));

const Team = ({ student, hideConferences, history }) => {
  const classes = useStyles();
  const user = useSelector((s) => s.user.user);

  const handleSchedule = (coach) => {
    coach.user_type === 'fa_coach'
      ? history.push(`/students/${student.user_id}/financial-aid`)
      : history.push(`/students/${student.user_id}/timeline`);
  };

  const getQuery = () => {
    switch (user.type) {
      case 'parent': {
        return GET_CONFERENCES;
      }
      case 'student': {
        return student.group ? GET_CIRCLE_CONFERENCES : GET_CONFERENCES;
      }
      case 'lead_coach': {
        return student.group ? GET_CIRCLE_COACH_CONFERENCES : GET_COACHES_CONFERENCES;
      }
      case 'head_coach': {
        return student.group ? GET_CIRCLE_COACH_CONFERENCES : GET_COACHES_CONFERENCES;
      }
      default: {
        return student.group ? GET_CIRCLE_CONFERENCES : GET_CONFERENCES;
      }
    }
  };

  const checkView = (c) => {
    switch (user.type) {
      case 'parent': {
        return true;
      }
      case 'student': {
        return true;
      }
      case 'lead_coach': {
        return (
          filter(
            c.conference_participants,
            (c) => c.user.user_type === 'fa_coach',
          ).length === 0
        );
      }
      case 'head_coach': {
        return (
          filter(
            c.conference_participants,
            (c) => c.user.user_type === 'fa_coach',
          ).length === 0
        );
      }
      default: {
        return true;
      }
    }
  };

  const CONF_QUERY = getQuery();
  const queryParams =
    user.type !== 'parent'
      ? student.group
        ? { id: student.group.group_id, userSub: student.sub }
        : { sub: student.sub }
      : { sub: user.sub };

  return (
    <Query query={GET_STUDENT_PROFILE} variables={{ id: student.id }}>
      {({ data, loading }) => {
        const profile = data?.student_profile[0];

        if (!profile) return <Spin spinning />;

        const coaches = [
          profile.lc_user,
          profile.hc_user,
          profile.fc_user,
        ].filter((i) => i);

        return (
          <Spin spinning={loading}>
            <Helmet title="My Team" />
            <Box marginBottom={4}>
              <Grid container spacing={4}>
                {coaches.map((coach, idx) => (
                  <Grid item xs={12} sm={4} key={idx}>
                    <BioCard profile={coach} handleSchedule={handleSchedule} />
                  </Grid>
                ))}
              </Grid>
            </Box>
            {!hideConferences && (
              <Query query={CONF_QUERY} variables={queryParams}>
                {({ data, loading }) => {
                  if (!data || loading) return <Spin spinning />;
                  const conferences = data.conferences;

                  const items = conferences
                    .filter((i) => i.conference_participants.length);

                  return <SessionsHistory conferences={items} />
                }}
              </Query>
            )}
          </Spin>
        );
      }}
    </Query>
  );
};

export default Team;
